// pages/AnnoncesPage.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Eye, Edit, Trash2, Send, FileText } from "lucide-react";
import AnnoncesList from "../../../components/features/annonces/AnnoncesList";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import PageListLayout from "../../../layouts/PageListLayout";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Annonce } from "../../../types/features/Annonce";
import useAnnonces from "../../../hooks/annonces/useAnnonces";

export default function AnnoncesPage() {
  const navigate = useNavigate();
  const { 
    annonces, 
    loading, 
    pagination,
    getAnnonces,
    deleteAnnonce,
    publishAnnonce
  } = useAnnonces();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAnnonce, setSelectedAnnonce] = useState<Annonce | null>(null);

  useEffect(() => {
    getAnnonces();
  }, []);

  const filteredAnnonces = annonces.filter(a =>
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (id: string) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette annonce ?")) {
      try {
        await deleteAnnonce(id);
        alertSuccess("Annonce supprimée avec succès");
      } catch (error) {
        alertError("Erreur lors de la suppression");
      }
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await publishAnnonce(id);
      alertSuccess("Annonce publiée avec succès, les emails ont été envoyés");
    } catch (error) {
      alertError("Erreur lors de la publication");
    }
  };

  const getActionsMenu = (annonce: Annonce): Menu[] => {
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/admin/annonces/details", { state: { annonce } });
          setSelectedAnnonce(null);
        },
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          navigate("/admin/annonces/update", { state: { annonce } });
          setSelectedAnnonce(null);
        },
      },
      ...(annonce.is_published ? [] : [{
        label: "Publier",
        icon: Send,
        onClick: () => {
          handlePublish(annonce.id);
          setSelectedAnnonce(null);
        },
        className: "text-green-600 hover:bg-green-50",
      }]),
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          handleDelete(annonce.id);
          setSelectedAnnonce(null);
        },
        className: "text-red-600 hover:bg-red-50",
      },
    ];
  };

  return (
    <PageListLayout title="">
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Annonces</h1>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Créez et gérez les annonces à destination des entreprises, agents et administrateurs
              </p>
              {pagination.total > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {pagination.total} annonce(s) au total
                </p>
              )}
            </div>

            <button
              onClick={() => navigate("/admin/annonces/new")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Nouvelle annonce</span>
            </button>
          </div>

          <div className="mt-6">
            <div className="relative max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher une annonce..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <AnnoncesList
            annonces={filteredAnnonces}
            onAction={(annonce) => setSelectedAnnonce(annonce)}
            selectable={false}
          />
        )}
      </div>

      {selectedAnnonce && (
        <MenuModal
          title={selectedAnnonce.title}
          isOpen={true}
          onClose={() => setSelectedAnnonce(null)}
          icon={null}
          menu={getActionsMenu(selectedAnnonce)}
        />
      )}
    </PageListLayout>
  );
}